import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Card, Col, Dropdown, Grid, message, Modal, Popconfirm, Row, Space} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import {
    createProjectDomain,
    createProjectDomainAll,
    deleteProjectDomainBatch,
    getProjectDomainList,
    updateProjectDomain,
    upsertProjectDomainBatch
} from '../../api/projectDomainApi';
import ProjectAgGridPanel from './ProjectAgGridPanel';
import ExcelUploadModal from '../ExcelUploadModal';
import ProjectDomainCreateModal from './ProjectDomainCreateModal';
import {AG_GRID_DEFAULT_COL_DEF} from '../../config/agGridConfig';
import SearchComponent from '../SearchComponent';
import useApiPermission from '../../hooks/useApiPermission';
import PermissionButton from '../common/PermissionButton';
import PermissionGate from '../common/PermissionGate';
import * as XLSX from 'xlsx';

// props: { pjtId, columns, pageSize }
const ProjectDomainsTab = ({ pjtId, columns, pageSize = 15 }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  // 검색 상태
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const gridRef = useRef();
  const screens = Grid.useBreakpoint();
  const isXs = !screens.md;

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getProjectDomainList);
  const canCreate = canCall(createProjectDomain);
  const canUploadUpsert = canCall(upsertProjectDomainBatch);
  const canUploadCreate = canCall(createProjectDomainAll); // upsert 없을 때 fallback
  const canUpload = canUploadUpsert || canUploadCreate;
  const canDeleteBatch = canCall(deleteProjectDomainBatch);
  const canUpdate = canCall(updateProjectDomain);

  // 검색 옵션 (DomainDataList와 유사)
  const searchOptions = [
    { value: 'all', label: '전체' },
    { value: 'domainGroupNm', label: '도메인그룹명' },
    { value: 'domainClsfNm', label: '도메인분류명' },
    { value: 'domainNm', label: '도메인명' },
    { value: 'domainExpln', label: '도메인설명' },
    { value: 'dataType', label: '데이터유형' },
    { value: 'unit', label: '단위' },
    { value: 'prmVl', label: '허용값' },
  ];

  // 백엔드 ReadProjectDomainResponse에 맞춘 기본 컬럼 구성 (작업 컬럼 제거)
  const defaultColumns = [
    { headerName: '', field: 'checkbox', headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true, width: 50, pinned: 'left', lockPosition: true, suppressMenu: true, sortable: false, filter: false, resizable: false },
    { headerName: '프로젝트도메인ID', field: 'pjtId', hide:true },
    { headerName: '프로젝트도메인ID', field: 'pjtDomainId', hide:true },
    { headerName: '도메인그룹명', field: 'domainGroupNm', minWidth: 160 },
    { headerName: '도메인분류명', field: 'domainClsfNm', minWidth: 160 },
    { headerName: '도메인명', field: 'domainNm', minWidth: 180, flex: 1 },
    { headerName: '도메인설명', field: 'domainExpln', minWidth: 220, flex: 1 },
    { headerName: '데이터유형', field: 'dataType', width: 120 },
    { headerName: '데이터길이', field: 'dataLen', width: 110 },
    { headerName: '소수점길이', field: 'dcptLen', width: 110 },
    { headerName: '저장형식', field: 'strgFrm', minWidth: 140 },
    { headerName: '표시형식', field: 'indctFrm', minWidth: 140 },
    { headerName: '단위', field: 'unit', width: 100 },
    { headerName: '허용값', field: 'prmVl', minWidth: 160 },
  ];

  const fetchList = useCallback(async () => {
    if (!pjtId) return;
    setLoading(true);
    setError('');
    try {
      if (permLoading) return;
      if (!canRead) { setRows([]); setError('목록 조회 권한이 없습니다.'); return; }
      const res = await getProjectDomainList(pjtId);
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
    } catch (e) {
      setError('프로젝트 도메인 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [pjtId, permLoading, canRead]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // 필터링된 데이터 생성
  const getFilteredData = () => {
    if (!searchText.trim()) return rows;
    const keyword = searchText.toLowerCase();
    const match = (val) =>
      val !== undefined && val !== null && String(val).toLowerCase().includes(keyword);

    return rows.filter((item) => {
      if (searchColumn === 'all') {
        return searchOptions
          .filter((opt) => opt.value !== 'all')
          .some((opt) => match(item[opt.value]));
      }
      return match(item[searchColumn]);
    });
  };

  const deleteSelected = async () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (!selectedNodes.length) { message.warning('삭제할 항목을 선택하세요.'); return; }
    const idList = selectedNodes.map((n) => n.data?.pjtDomainId).filter(Boolean);
    if (!idList.length) { message.warning('유효한 항목이 없습니다.'); return; }
    try {
      await deleteProjectDomainBatch(pjtId, idList);
      message.success(`${idList.length}개 항목을 삭제했습니다.`);
      await fetchList();
      gridRef.current.api.deselectAll();
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    }
  };

  // 신규 XLSX 다운로드 함수
  const handleDownloadExcel = () => {
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const api = gridRef.current.api;
    const exportColumns = [
      { header: '프로젝트도메인ID', field: 'pjtDomainId' },
      { header: '도메인그룹명', field: 'domainGroupNm' },
      { header: '도메인분류명', field: 'domainClsfNm' },
      { header: '도메인명', field: 'domainNm' },
      { header: '도메인설명', field: 'domainExpln' },
      { header: '데이터유형', field: 'dataType' },
      { header: '데이터길이', field: 'dataLen' },
      { header: '소수점길이', field: 'dcptLen' },
      { header: '저장형식', field: 'strgFrm' },
      { header: '표시형식', field: 'indctFrm' },
      { header: '단위', field: 'unit' },
      { header: '허용값', field: 'prmVl' },
    ];
    const header = exportColumns.map(c => c.header);
    const dataRows = [];
    api.forEachNodeAfterFilterAndSort(node => {
      const row = exportColumns.map(c => node.data?.[c.field] ?? '');
      dataRows.push(row);
    });
    if (!dataRows.length) { message.info('다운로드할 데이터가 없습니다.'); return; }
    const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    ws['!cols'] = header.map(h => ({ wch: Math.min(40, Math.max(12, h.length + 4)) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ProjectDomains');
    XLSX.writeFile(wb, `project-domains_${pjtId}.xlsx`);
  };

  // 엑셀 업로드 미리보기용 컬럼과 필드 매핑 (모든 필드 포함)
  const excelColumnDefs = [
    { headerName: '프로젝트도메인ID', field: 'pjtDomainId', editable: false, width: 220 },
    { headerName: '도메인그룹명*', field: 'domainGroupNm', editable: true, width: 160 },
    { headerName: '도메인분류명*', field: 'domainClsfNm', editable: true, width: 160 },
    { headerName: '도메인명*', field: 'domainNm', editable: true, width: 180 },
    { headerName: '도메인설명*', field: 'domainExpln', editable: true, minWidth: 220, flex: 1 },
    { headerName: '데이터유형*', field: 'dataType', editable: true, width: 140 },
    { headerName: '데이터길이*', field: 'dataLen', editable: true, width: 120 },
    { headerName: '소수점길이', field: 'dcptLen', editable: true, width: 120 },
    { headerName: '저장형식*', field: 'strgFrm', editable: true, width: 140 },
    { headerName: '표시형식', field: 'indctFrm', editable: true, width: 140 },
    { headerName: '단위', field: 'unit', editable: true, width: 100 },
    { headerName: '허용값', field: 'prmVl', editable: true, minWidth: 140 },
  ];

  const excelFieldMapping = [
    'pjtDomainId',
    'domainGroupNm',
    'domainClsfNm',
    'domainNm',
    'domainExpln',
    'dataType',
    'dataLen',
    'dcptLen',
    'strgFrm',
    'indctFrm',
    'unit',
    'prmVl',
  ];

  const excelGuide = 'A: 프로젝트도메인ID | B: *도메인그룹명 | C: *도메인분류명 | D: *도메인명 | E: *도메인설명 | F: *데이터유형 | G: 데이터길이 | H: 소수점길이 | I: *저장형식 | J: 표시형식 | K: 단위 | L: 허용값';

  const openEdit = useCallback((row) => {
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    setEditItem(row);
    setIsEditModalOpen(true);
  }, [permLoading, canUpdate]);

  const handleEditSelected = useCallback(() => {
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selected = gridRef.current.api.getSelectedNodes();
    if (selected.length !== 1) { message.warning('수정할 항목 1건을 선택하세요.'); return; }
    openEdit(selected[0].data);
  }, [permLoading, canUpdate, openEdit]);

  // 작은 화면 드롭다운용 삭제 확인
  const confirmDeleteFromMenu = () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (!selectedNodes.length) { message.warning('삭제할 항목을 선택하세요.'); return; }
    Modal.confirm({ title: '선택 항목 삭제', content: '선택된 항목들을 삭제하시겠습니까?', okText: '삭제', okButtonProps: { danger: true }, cancelText: '취소', onOk: deleteSelected });
  };

  const actionMenuItems = [
    ...(canCreate ? [{ key: 'create', icon: <PlusOutlined />, label: '신규생성' }] : []),
    ...(canUpdate ? [{ key: 'edit', icon: <EditOutlined />, label: '선택 항목 수정' }] : []),
    ...(canDeleteBatch ? [{ type: 'divider' }, { key: 'delete', icon: <DeleteOutlined />, label: '선택 항목 삭제', danger: true }] : []),
    ...(canUpload ? [{ type: 'divider' }, { key: 'upload', icon: <UploadOutlined />, label: '엑셀 업로드' }] : []),
    { key: 'download', icon: <DownloadOutlined />, label: '엑셀 다운로드' },
    ...(canRead ? [{ type: 'divider' }, { key: 'refresh', icon: <ReloadOutlined />, label: '새로고침' }] : []),
  ];

  const onActionMenuClick = ({ key }) => {
    switch (key) {
      case 'create': (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true); break;
      case 'edit': handleEditSelected(); break;
      case 'delete': confirmDeleteFromMenu(); break;
      case 'upload': (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true); break;
      case 'download': handleDownloadExcel(); break;
      case 'refresh': (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList(); break;
      default: break;
    }
  };

  const getContextMenuItems = useCallback((params) => {
    const items = [];
    if (params?.node?.data) {
      items.push({ name: '수정', action: () => openEdit(params.node.data) });
      items.push('separator');
    }
    // 기본 제공 항목 일부
    items.push('copy');
    items.push('separator');
    items.push('export');
    return items;
  }, [openEdit]);

  return (
    <PermissionGate require={getProjectDomainList}>
      {({allowed}) => (
        <div style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24}>
                {isXs ? (
                  // ...existing small screen with Dropdown (edit 항목 유지)...
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SearchComponent
                        searchColumn={searchColumn}
                        setSearchColumn={setSearchColumn}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        searchOptions={searchOptions}
                        defaultSearchColumn={'domainGroupNm'}
                      />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} placement="bottomRight" trigger={["click"]}>
                        <Button aria-label="작업 메뉴" icon={<EllipsisOutlined />} />
                      </Dropdown>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                      <SearchComponent
                        searchColumn={searchColumn}
                        setSearchColumn={setSearchColumn}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        searchOptions={searchOptions}
                        defaultSearchColumn={'domainGroupNm'}
                      />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                      <Space wrap>
                        <PermissionButton type="primary" icon={<PlusOutlined />} onClick={() => (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true)} disabled={permLoading || !canCreate} require={createProjectDomain}>신규생성</PermissionButton>
                        <PermissionButton icon={<EditOutlined />} type="default" onClick={handleEditSelected} disabled={permLoading || !canUpdate} require={updateProjectDomain}>선택수정</PermissionButton>
                        <Popconfirm title="선택 항목 삭제" description="선택된 항목들을 삭제하시겠습니까?" onConfirm={deleteSelected} okText="삭제" cancelText="취소" disabled={permLoading || !canDeleteBatch}>
                          <PermissionButton danger icon={<DeleteOutlined />} disabled={permLoading || !canDeleteBatch} require={deleteProjectDomainBatch}>선택 항목 삭제</PermissionButton>
                        </Popconfirm>
                        <PermissionButton type="primary" icon={<UploadOutlined />} onClick={() => (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canUpload} require={canUploadUpsert ? upsertProjectDomainBatch : createProjectDomainAll} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                          {'엑셀 업로드'}
                        </PermissionButton>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadExcel} style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>엑셀 다운로드</Button>
                        <PermissionButton icon={<ReloadOutlined />} onClick={() => (permLoading || !canRead) ? message.warning('조회 권한이 ��습��다.') : fetchList()} disabled={permLoading || !canRead} require={getProjectDomainList}>새로고침</PermissionButton>
                      </Space>
                    </div>
                  </div>
                )}
              </Col>
            </Row>

          </Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <ProjectAgGridPanel
              gridRef={gridRef}
              rowData={getFilteredData()}
              columnDefs={columns || defaultColumns}
              pageSize={pageSize}
              loading={loading}
              error={error}
              defaultColDef={AG_GRID_DEFAULT_COL_DEF}
              onRowDoubleClicked={(e) => e?.data && openEdit(e.data)}
              getContextMenuItems={getContextMenuItems}
            />
          </div>

          <ExcelUploadModal
            isOpen={isExcelModalOpen}
            onClose={() => setIsExcelModalOpen(false)}
            onSuccess={fetchList}
            agGridColumnDefs={excelColumnDefs}
            agGridDefaultColDef={AG_GRID_DEFAULT_COL_DEF}
            fieldMapping={excelFieldMapping}
            uploadApi={(rows) => {
              const clean = rows.map(r => {
                const trim = v => (typeof v === 'string' ? v.trim() : v);
                const nz = v => {
                  const t = trim(v);
                  return t === '' ? null : t;
                };
                return {
                  pjtDomainId: nz(r.pjtDomainId),
                  domainGroupNm: trim(r.domainGroupNm) || '',
                  domainClsfNm: trim(r.domainClsfNm) || '',
                  domainNm: trim(r.domainNm) || '',
                  domainExpln: trim(r.domainExpln) || '',
                  dataType: trim(r.dataType) || '',
                  dataLen: r.dataLen === '' ? null : r.dataLen,
                  dcptLen: r.dcptLen === '' ? null : r.dcptLen,
                  strgFrm: trim(r.strgFrm) || '',
                  indctFrm: nz(r.indctFrm),
                  unit: nz(r.unit),
                  prmVl: nz(r.prmVl),
                };
              });
              // 업서트 권한 있으면 업서트, 없으면 단순 배치 생성
              if (canUploadUpsert) {
                return upsertProjectDomainBatch(pjtId, clean);
              }
              return createProjectDomainAll(pjtId, clean);
            }}
            excelGuide={excelGuide + (canUploadUpsert ? '\n* 업서트 가능: 기존 pjtDomainId 일치 시 업데이트, 없으면 생성' : '\n* 업서트 권한 없음: 모든 행을 신규 생성 시도')}
            rowKey="rowKey"
          />

          <ProjectDomainCreateModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={fetchList}
            pjtId={pjtId}
          />
          <ProjectDomainCreateModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSuccess={async () => { await fetchList(); setEditItem(null); }}
            pjtId={pjtId}
            mode="edit"
            initialValues={editItem}
          />
        </div>
      )}
    </PermissionGate>
  );
};

export default ProjectDomainsTab;
